import {isBoolean} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler, Renderer, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';

/*
 * Props.
 */

interface SettingsObjectPickerHeaderProps {
  columns: ReadonlyArray<ReactNode>;
  onHeaderChange: Handler;
  isHeaderChecked?: boolean;
  isHeaderIndeterminate?: boolean;
  renderHeaderDropdownTitle?: Renderer;
  renderHeaderDropdownLabel?: Renderer;
  renderHeaderDropdown?: RendererOf<PopoverTransferProps>;
  isSubHeaderChecked?: boolean;
  subHeaderLabel?: ReactNode;
  onSubHeaderChange?: Handler;
}

/*
 * Style.
 */

const StyledTableHeaderRightDiv = styled.div`
  padding: 15px 16px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 10px;
  align-items: center;
`;

const StyledDropdownLabelDiv = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  line-height: 17px;
  color: #9b9b9b;
`;

const StyledDropdownButton = styled(ChromelessButton)`
  line-height: 16px;
`;

const StyledTableHeaderDiv = styled.div`
  background-color: #f8f8f8;
  border-bottom: 1px solid #e0e0e0;
  ${(p) => boxShadow(p.theme, `0px 2px 14px rgba(19, 24, 57, 0.06)`)};

  display: grid;
  grid-template-columns: 1fr auto;
  grid-auto-rows: auto;
`;

const StyledTableHeaderLeftDiv = styled.div`
  padding: 20px 16px;
  padding-right: 0;
  display: flex;

  & ~ & {
    padding-top: 0;
  }

  cursor: pointer;

  label > div:last-child > span {
    display: inline-block;
  }
`;

const styledTableHeaderStyles = css`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: #383838;
  margin-left: 16px;
`;

const StyledTableHeaderTitle = styled.div`
  ${styledTableHeaderStyles};
`;

const StyledTableCellWithColumns = styled.div<{$columns: number}>`
  display: inline-grid;
  grid-column-gap: 4px;
  grid-template-columns: repeat(${(p) => p.$columns}, 1fr);
`;

const StyledTableHeaderTitleWithColumns = styled(StyledTableCellWithColumns)`
  ${styledTableHeaderStyles};
  flex: 1;
`;

const StyledTableHeaderWithColumns = styled(StyledTableHeaderLeftDiv)`
  padding-right: 16px;
`;

/*
 * Component.
 */

export const SettingsObjectPickerHeader: FC<SettingsObjectPickerHeaderProps> = (props) => {
  const {
    renderHeaderDropdown,
    renderHeaderDropdownLabel,
    renderHeaderDropdownTitle,
    isSubHeaderChecked,
    subHeaderLabel,
    onSubHeaderChange,
  } = props;
  const maybeRenderHeaderAction = () => {
    if (!renderHeaderDropdown || !renderHeaderDropdownLabel || !renderHeaderDropdownTitle) {
      return null;
    }

    return (
      <StyledTableHeaderRightDiv>
        <StyledDropdownLabelDiv>{renderHeaderDropdownLabel()}</StyledDropdownLabelDiv>
        <PopoverCoordinator
          renderButton={() => (
            <StyledDropdownButton className="settings-dropdown">
              {renderHeaderDropdownTitle()}
            </StyledDropdownButton>
          )}
          renderModal={renderHeaderDropdown}
        />
      </StyledTableHeaderRightDiv>
    );
  };

  const hasHeaderWithAction = renderHeaderDropdown && renderHeaderDropdownLabel && renderHeaderDropdownTitle;
  const hasSubHeader = isBoolean(isSubHeaderChecked) && Boolean(subHeaderLabel) && Boolean(onSubHeaderChange);

  if (!hasHeaderWithAction && !hasSubHeader) {
    return (
      <StyledTableHeaderDiv>
        <StyledTableHeaderWithColumns>
          <CheckBox
            isIndeterminate={props.isHeaderIndeterminate}
            isChecked={props.isHeaderChecked}
            onChange={props.onHeaderChange}
          />
          <StyledTableHeaderTitleWithColumns $columns={props.columns.length}>
            {props.columns.map((col) => (
              <div key={String(col)}>{col}</div>
            ))}
          </StyledTableHeaderTitleWithColumns>
        </StyledTableHeaderWithColumns>
      </StyledTableHeaderDiv>
    );
  }

  const maybeRenderSubHeader = () => {
    if (!isSubHeaderChecked || !onSubHeaderChange || !subHeaderLabel) {
      return null;
    }

    return (
      <StyledTableHeaderLeftDiv>
        <CheckBox isChecked={isSubHeaderChecked} onChange={onSubHeaderChange}>
          <StyledTableHeaderTitle>{subHeaderLabel}</StyledTableHeaderTitle>
        </CheckBox>
      </StyledTableHeaderLeftDiv>
    );
  };

  return (
    <StyledTableHeaderDiv>
      <StyledTableHeaderLeftDiv>
        <CheckBox
          isIndeterminate={props.isHeaderIndeterminate}
          isChecked={props.isHeaderChecked}
          isDisabled={hasSubHeader}
          onChange={props.onHeaderChange}
        />
        <StyledTableHeaderTitle>{props.columns[0]}</StyledTableHeaderTitle>
      </StyledTableHeaderLeftDiv>
      {maybeRenderHeaderAction()}
      {maybeRenderSubHeader()}
    </StyledTableHeaderDiv>
  );
};
